import React, { useMemo, useState, useCallback, useEffect } from "react";
import { ChevronRight, File, FileText, Folder, FolderOpen } from "lucide-react";
import { SiInstagram, SiMeta, SiTiktok } from "react-icons/si";
import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";

function formatBytes(n) {
  if (n == null || Number.isNaN(n)) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function buildDirNode(name) {
  return { type: "dir", name, children: new Map(), files: [] };
}

export function buildZipExplorerTree(entries) {
  const root = buildDirNode("");
  for (const e of entries || []) {
    const parts = String(e.path || "").split("/").filter(Boolean);
    if (!parts.length) continue;
    let node = root;
    for (let i = 0; i < parts.length - 1; i += 1) {
      const seg = parts[i];
      if (!node.children.has(seg)) node.children.set(seg, buildDirNode(seg));
      node = node.children.get(seg);
    }
    const fn = parts[parts.length - 1];
    node.files.push({ ...e, displayName: fn });
  }

  function sortNode(node) {
    node.files.sort((a, b) => a.displayName.localeCompare(b.displayName));
    for (const child of node.children.values()) {
      sortNode(child);
    }
  }
  sortNode(root);
  return root;
}

/** Instagram / Meta (Facebook) / TikTok hints from asset paths (e.g. campaign bundle filenames). */
function detectZipEntryBrand(path) {
  const p = String(path || "").toLowerCase();
  if (p.includes("instagram")) return "instagram";
  if (p.includes("tiktok")) return "tiktok";
  if ((/\bmeta\b/.test(p) && !/\bmetadata\b/.test(p)) || p.includes("facebook")) return "meta";
  return null;
}

function ZipEntryFileGlyph({ entry }) {
  const brand = detectZipEntryBrand(entry.path);
  const base = "h-4 w-4 shrink-0";
  if (brand === "instagram") {
    return <SiInstagram className={cn(base, "text-[#E4405F]")} aria-hidden />;
  }
  if (brand === "meta") {
    return <SiMeta className={cn(base, "text-[#0668E1] dark:text-[#4BA3FF]")} aria-hidden />;
  }
  if (brand === "tiktok") {
    return <SiTiktok className={cn(base, "text-foreground")} aria-hidden />;
  }
  if (entry.kind === "text") {
    return <FileText className={cn(base, "text-muted-foreground")} aria-hidden />;
  }
  if (String(entry.path || "").toLowerCase().endsWith(".pdf")) {
    return <FileText className={cn(base, "text-red-600/90 dark:text-red-400/90")} aria-hidden />;
  }
  return <File className={cn(base, "text-muted-foreground")} aria-hidden />;
}

function DirRow({ name, depth, open, onToggle, accentRing }) {
  return (
    <button
      type="button"
      className={cn(
        "flex w-full items-center gap-1 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted/60",
        accentRing && "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E85D3F]/35"
      )}
      style={{ paddingLeft: 8 + depth * 14 }}
      onClick={onToggle}
    >
      <ChevronRight
        className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", open && "rotate-90")}
        aria-hidden
      />
      {open ? (
        <FolderOpen className="h-4 w-4 shrink-0 text-amber-600/90 dark:text-amber-400/90" aria-hidden />
      ) : (
        <Folder className="h-4 w-4 shrink-0 text-amber-600/90 dark:text-amber-400/90" aria-hidden />
      )}
      <span className="min-w-0 truncate font-medium text-foreground">{name}</span>
    </button>
  );
}

function FileRow({ entry, depth, selected, onSelectEntry, accentRing }) {
  return (
    <button
      type="button"
      className={cn(
        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted/60",
        selected && "bg-muted/80 ring-1 ring-border",
        accentRing && "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E85D3F]/35"
      )}
      style={{ paddingLeft: 8 + depth * 14 }}
      onClick={() => onSelectEntry(entry)}
    >
      <span className="inline-block w-4 shrink-0" aria-hidden />
      <ZipEntryFileGlyph entry={entry} />
      <span className="min-w-0 flex-1 truncate text-foreground">{entry.displayName}</span>
      <span className="shrink-0 tabular-nums text-[11px] text-muted-foreground">{formatBytes(entry.size)}</span>
    </button>
  );
}

function TreeBranch({ node, depth, expanded, toggle, selectedPath, onSelectEntry, accentRing }) {
  const dirs = [...node.children.keys()].sort((a, b) => a.localeCompare(b));
  return (
    <>
      {dirs.map((dirName) => {
        const child = node.children.get(dirName);
        const pathKey = `${depth}:${dirName}`;
        const open = expanded.has(pathKey);
        return (
          <div key={dirName}>
            <DirRow
              name={dirName}
              depth={depth}
              open={open}
              accentRing={accentRing}
              onToggle={() => toggle(pathKey)}
            />
            {open ? (
              <TreeBranch
                node={child}
                depth={depth + 1}
                expanded={expanded}
                toggle={toggle}
                selectedPath={selectedPath}
                onSelectEntry={onSelectEntry}
                accentRing={accentRing}
              />
            ) : null}
          </div>
        );
      })}
      {node.files.map((f) => (
        <FileRow
          key={f.path}
          entry={f}
          depth={depth}
          selected={selectedPath === f.path}
          accentRing={accentRing}
          onSelectEntry={onSelectEntry}
        />
      ))}
    </>
  );
}

/**
 * Folder tree + preview for ZIP-like entry lists (bundle virtual entries or parsed ZIP).
 */
export default function ChatZipArchiveExplorer({
  entries,
  selectedPath,
  onSelectPath,
  resolvePdfAttachmentForEntry,
  onOpenPdfAttachment,
  listNotice,
  accentRing = true,
  className,
}) {
  const tree = useMemo(() => buildZipExplorerTree(entries), [entries]);

  const defaultExpanded = useMemo(() => {
    const set = new Set();
    function walk(node, depth) {
      [...node.children.keys()].forEach((dirName) => {
        set.add(`${depth}:${dirName}`);
        walk(node.children.get(dirName), depth + 1);
      });
    }
    walk(tree, 0);
    return set;
  }, [tree]);

  const [expanded, setExpanded] = useState(() => defaultExpanded);

  useEffect(() => {
    setExpanded(defaultExpanded);
  }, [defaultExpanded]);

  const toggle = useCallback((pathKey) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(pathKey)) next.delete(pathKey);
      else next.add(pathKey);
      return next;
    });
  }, []);

  const selectedEntry = useMemo(
    () => (entries || []).find((e) => e.path === selectedPath) || null,
    [entries, selectedPath]
  );

  const pdfOpenable = useMemo(() => {
    if (!selectedEntry || typeof resolvePdfAttachmentForEntry !== "function") return null;
    const att = resolvePdfAttachmentForEntry(selectedEntry);
    return att?.type === "pdf" && typeof att.data === "string" && att.data.length > 0 ? att : null;
  }, [selectedEntry, resolvePdfAttachmentForEntry]);

  const handleSelectEntry = useCallback(
    (entry) => {
      const att =
        typeof resolvePdfAttachmentForEntry === "function" ? resolvePdfAttachmentForEntry(entry) : null;
      if (
        att?.type === "pdf" &&
        typeof att.data === "string" &&
        att.data.length > 0 &&
        typeof onOpenPdfAttachment === "function"
      ) {
        onOpenPdfAttachment(att);
        return;
      }
      onSelectPath(entry.path);
    },
    [resolvePdfAttachmentForEntry, onOpenPdfAttachment, onSelectPath]
  );

  return (
    <div className={cn("flex min-h-0 flex-1 flex-col gap-0 overflow-hidden", className)}>
      <div className="chat-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain border-b border-border/60 px-1 py-2">
        {listNotice ? (
          <p className="mb-2 px-2 text-xs text-muted-foreground">{listNotice}</p>
        ) : null}
        <TreeBranch
          node={tree}
          depth={0}
          expanded={expanded}
          toggle={toggle}
          selectedPath={selectedPath}
          onSelectEntry={handleSelectEntry}
          accentRing={accentRing}
        />
      </div>
      <div className="flex max-h-[45%] min-h-[120px] shrink-0 flex-col border-t border-border/40 bg-background/50">
        <div className="border-b border-border/50 px-3 py-2">
          <p className="truncate text-xs font-semibold uppercase tracking-wide text-muted-foreground">Preview</p>
          <p className="truncate text-sm font-medium text-foreground" title={selectedEntry?.path}>
            {selectedEntry?.path || "Select a file"}
          </p>
        </div>
        <div className="chat-scrollbar min-h-0 flex-1 overflow-y-auto px-3 py-3">
          {!selectedEntry ? (
            <p className="text-sm text-muted-foreground">Choose a file from the archive to preview text contents.</p>
          ) : selectedEntry.kind === "text" ? (
            <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-relaxed text-foreground">
              {selectedEntry.text || ""}
            </pre>
          ) : pdfOpenable ? (
            <div className="flex flex-col gap-3 text-sm">
              <p className="text-muted-foreground">
                {selectedEntry.bundledAssistantPdf
                  ? "This PDF is included with your downloadable ZIP. Open it in the PDF viewer."
                  : "Open this PDF in the side-by-side PDF viewer."}
              </p>
              {typeof onOpenPdfAttachment === "function" ? (
                <Button
                  type="button"
                  size="sm"
                  className="w-fit bg-violet-600 hover:bg-violet-700"
                  onClick={() => onOpenPdfAttachment(pdfOpenable)}
                >
                  Open PDF preview
                </Button>
              ) : null}
              <p className="text-xs text-muted-foreground">{formatBytes(selectedEntry.size)} — tip: click the file in the list to jump straight to the PDF viewer.</p>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">
              <p>No inline preview for this file type.</p>
              {selectedEntry.hint ? <p className="mt-2 text-foreground/90">{selectedEntry.hint}</p> : null}
              <p className="mt-2">{formatBytes(selectedEntry.size)} uncompressed — included when you download.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
